import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AddCircleOutline, FilterListOff } from "@mui/icons-material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import Filter from "../../components/Filter";
import { Typography, Button, Box, Tooltip } from "@mui/material";
import { Delete, Deletewhite } from "images";
import IconButton from "@mui/material/IconButton";
import { ModalPopup } from "_components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  deleteMapRequestApi,
  getMapRequestApi,
  getMapRequestByIdApi,
  postMapRequestFilterApi,
} from "services/mapCenter/mapCenter.thunks";
import { alertActions, authActions } from "_store";
import { ERR_MSG, TOOLTIP_MSG } from "constants/labels";
import dayjs from "dayjs";
import { colFilterDatePattern } from "_utils/colFilterDatePattern";
import HistoryIcon from "@mui/icons-material/History";
import ViewHistory from "container/mapCenter/components/Modal/ViewHistoryModal/ViewHistory";
import ReviewModal from "container/mapCenter/components/Modal/ReviewModal/ReviewModal";
import RateReviewSharpIcon from "@mui/icons-material/RateReviewSharp";
import { saveFiltersToLocalStore } from "_utils/searchFilterLocalStorage";

const MapView = ({
  isAdmin = false,
  mapRequestList = [],
  setMapRequestList,
  filterParams,
  setFilterParams,
}) => {
  const auth = useSelector((x) => x.auth.value);
  const isMCUserExpired = auth?.Data?.UserDetails?.Status === "Expired";
  const isMCUserPartiallyApproved =
    auth?.Data?.UserDetails?.Status === "Partially Approved";
  const isMCUserPartiallyVerified =
    auth?.Data?.UserDetails?.Status === "Partially Verified";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEnabledToolbar, setIsEnabledToolbar] = useState(false);
  const [selectedMapReqIds, setSelectedMapReqIds] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [openHistory, setOpenHistory] = useState(false);
  const [historyId, setHistoryId] = useState("");
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [mapRequestByIdData, setMapRequestByIdData] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logout = () => dispatch(authActions.logout());
  const header = "Map Center";

  const handleCreateMapRequestClick = useCallback(
    () => navigate("/map-request-create"),
    [isAdmin, navigate],
  );

  const handleProcessMapRequestClick = useCallback((e, mapRequestId) => {
    if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      navigate(`/map-request-${isAdmin ? "process" : "view"}/${mapRequestId}`);
    }
  }, []);

  const handleDeleteClick = useCallback(async () => {
    if (selectedMapReqIds.length) {
      try {
        const response = await dispatch(
          deleteMapRequestApi(selectedMapReqIds),
        ).unwrap();
        const { Succeeded, Message } = response;

        if (Succeeded) {
          setMapRequestList((prev) => {
            const exclude = new Set(selectedMapReqIds);
            return prev.filter((m) => !exclude.has(m.MapRequestId));
          });
          dispatch(
            alertActions.success({
              message: Message,
            }),
          );
        } else {
          dispatch(
            alertActions.error({
              message: Message || ERR_MSG["073"],
            }),
          );
        }
      } catch (error) {
        dispatch(
          alertActions.error({
            message: error.message ? ERR_MSG["073"] : error,
          }),
        );
      }
      setRowSelection({});
    }

    setIsModalOpen(0);
  }, [isAdmin, selectedMapReqIds]);

  const handleOpenDeleteModal = useCallback((mapReqId) => {
    setIsModalOpen(1);
    setSelectedMapReqIds(Array.isArray(mapReqId) ? mapReqId : [mapReqId]);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsModalOpen(0);
    setSelectedMapReqIds([]);
  }, []);

  const handleFilterSubmit = async (payload) => {
    try {
      dispatch(alertActions.clear());
      const result = await dispatch(postMapRequestFilterApi(payload)).unwrap();
      if (result?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: result?.Message,
            header: header,
          }),
        );
        setFilterParams(payload)
        return;
      }

      setMapRequestList(result?.Data);
      payload && setFilterParams(payload);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        }),
      );
    }
  };

  const handleClearAllFilters = async () => {
    table && table.resetColumnFilters();
    setFilterParams(null);
    setRowSelection({});
    try {
      dispatch(alertActions.clear());
      const result = await dispatch(getMapRequestApi()).unwrap();
      if (!result?.Succeeded) {
        dispatch(
          alertActions.error({
            message: result?.Message,
            header: header,
          }),
        );
        return;
      }
      setMapRequestList(result?.Data);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        }),
      );
    }
  };

  const colStdUser = ["MapRequestId", "Address", "RequestDate", "Status"];
  const colAdminUser = [
    "MapRequestId",
    "Address",
    "MapCenterRequestType",
    "Requestor",
    "Company",
    "RequestDate",
    "Status",
    "Location",
    "MapRequestType",
    "DaysToComplete",
  ];
  const columnOrder = useMemo(
    () => (isAdmin ? colAdminUser : colStdUser),
    [isAdmin],
  );
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: "MapRequestId",
        header: "Request #",
        muiTableBodyCellProps: {
          sx: {
            textAlign: "center",
          },
        },
        muiTableHeadCellProps: {
          sx: {
            textAlign: "center",
          },
        },
        Cell: ({ row }) => {
          const mapRequestId = row.original.MapRequestId;
          return (
            <Tooltip
              title={`Click to ${isAdmin ? "process" : "view"} map request.`}
              arrow
            >
              <a
                href={`/map-request-${
                  isAdmin ? "process" : "view"
                }/${mapRequestId}`}
                onClick={(e) => handleProcessMapRequestClick(e, mapRequestId)}
                style={{
                  display: "block",
                  color: "#4fa4ef",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                {mapRequestId}
              </a>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "MapCenterRequestType",
        header: "Request Type",
        Cell: ({ row }) => {
          const data = row.original.MapCenterRequestType;
          return data;
        },
      },
      {
        accessorKey: "Address",
        header: "Address",
      },
      {
        accessorKey: "MapRequestType",
        header: "Requestor Type",
        size: 300,
      },
      {
        accessorKey: "Company",
        header: "Company",
      },
      {
        accessorKey: "Requestor",
        header: "Requestor Name",
        size: 300,
      },
      {
        accessorKey: "DaysToComplete",
        header: "Days to complete",
        size: 300,
      },
      {
        accessorKey: "RequestDate",
        header: "Date",
        Cell: ({ row }) => {
          const rawDate = row.original.RequestDate;
          return dayjs(rawDate).format("MMMM D, YYYY");
        },
        filterFn: (row, id, filterValue) => {
          return colFilterDatePattern(row.original.RequestDate, filterValue);
        },
      },
      {
        accessorKey: "Location",
        header: "Location",
      },
      {
        accessorKey: "Status",
        header: "Status",
      },
    ];
    const orderIndex = new Map(columnOrder.map((k, i) => [k, i]));
    const updatedBaseColumns = baseColumns
      .filter((col) => orderIndex.has(col.accessorKey))
      .sort(
        (a, b) => orderIndex.get(a.accessorKey) - orderIndex.get(b.accessorKey),
      );

    return updatedBaseColumns;
  }, [mapRequestList, isAdmin]);

  const table = useMaterialReactTable(
    {
      columns: columns,
      data: mapRequestList || [],
      enableHiding: false,
      enableSorting: true,
      enableColumnFilters: true,
      columnFilterDisplayMode: "popover",
      enableFullScreenToggle: false,
      enableColumnActions: false,
      enableColumnOrdering: false,
      enablePagination: true,
      enableGlobalFilter: false,
      enableDensityToggle: false,
      enableTopToolbar: true,
      enableRowSelection: isAdmin,
      state: { rowSelection },
      onRowSelectionChange: setRowSelection,
      enableRowActions: true,
      enableStickyHeader: true,
      positionActionsColumn: "last",
      enableSelectAll: true,
      paginationDisplayMode: "pages",
      initialState: { columnOrder },
      muiTableBodyProps: {
        sx: {
          maxHeight: "none",
          overflowY: "visible",
        },
      },
      muiTableContainerProps: {
        sx: {
          overflowX: "auto",
          overflowY: "unset",
          maxHeight: "unset",
          width: "100%",
        },
      },
      muiTableHeadCellProps: { sx: { textAlign: "center" } },
      muiTableBodyCellProps: { sx: { textAlign: "center" } },
      muiToolbarAlertBannerProps: {
        sx: { display: "none" },
      },
      muiSelectCheckboxProps: ({ row }) => ({
        disabled: !(
          row.original.Status === "Pending" ||
          row.original.Status === "Submitted"
        ),
      }),
      muiSelectAllCheckboxProps: {
        disabled: mapRequestList?.some(
          (row) => !(row.Status === "Pending" || row.Status === "Submitted"),
        ),
      },
      renderTopToolbarCustomActions: ({ table }) => {
        const selectedRows = table.getSelectedRowModel().rows;
        const selectedCount = selectedRows.length;
        const selectedIds = selectedRows.map((r) => r.original.MapRequestId);
        setIsEnabledToolbar(!selectedCount);

        return (
          <Box
            sx={{
              display: "flex",
              gap: "16px",
              padding: "8px",
              flexWrap: "wrap",
            }}
          >
            <Tooltip title="Clear All Filters" className="Deactivate">
              <div>
                <IconButton onClick={handleClearAllFilters}>
                  <FilterListOff variant="contained" color="secondary" />
                </IconButton>
              </div>
            </Tooltip>
            {isAdmin && (
              <Tooltip
                title={
                  !isEnabledToolbar
                    ? `Delete selected map request${
                        selectedCount > 1 ? "s" : ""
                      }`
                    : ""
                }
                className="DeleteSelected"
              >
                <div>
                  <IconButton
                    className="delete"
                    onClick={() => handleOpenDeleteModal(selectedIds)}
                    disabled={isEnabledToolbar}
                  >
                    <img
                      src={Deletewhite}
                      alt="Delete"
                      className="downloadIconSize"
                    />
                  </IconButton>
                </div>
              </Tooltip>
            )}
          </Box>
        );
      },
      renderRowActions: ({ row }) => {
        const { MapRequestId: mapRequestId, Status: status } = row.original;
        const isDelBtnDisabled = !(
          status === "Pending" || status === "Submitted"
        );
        const isEmailed = status === "Emailed"
        const handleOpenHistory = () => {
          setOpenHistory(true);
          setHistoryId(mapRequestId);
        };

        const fetchMapRequestById = async (mapReqId) => {
          dispatch(alertActions.clear());
          try {
            const res = await dispatch(getMapRequestByIdApi(mapReqId)).unwrap();
            if (res?.Succeeded === false) {
              dispatch(
                alertActions.error({
                  message: res?.Message,
                  header: header,
                }),
              );
              return;
            }
            setMapRequestByIdData(res?.Data);
          } catch (error) {
            dispatch(
              alertActions.error({
                message: error.message ? ERR_MSG["073"] : error,
                header: header,
              }),
            );
          }
        };

        const handleOpenReviewModal = () => {
          fetchMapRequestById(mapRequestId);
          setOpenReviewModal(true);
          setHistoryId(mapRequestId);
        };

        return (
          <Typography
            component={"div"}
            sx={{
              width: "100%",
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
            }}
            className="tableicons"
          >
            <Tooltip
              title={
                isDelBtnDisabled && isAdmin
                  ? TOOLTIP_MSG.MAP_CENTER.DISABLED_BTN
                  : ""
              }
              placement="top"
            >
              <div>
                {isAdmin ? (
                  <IconButton className="delete" disabled={isDelBtnDisabled}>
                    <img
                      src={Delete}
                      alt="Delete"
                      onClick={() => handleOpenDeleteModal(mapRequestId)}
                    ></img>
                  </IconButton>
                ) : (
                  <>                    
                    <IconButton>
                      <HistoryIcon onClick={handleOpenHistory} />
                    </IconButton>                      
                    {!isEmailed && (
                      <IconButton>
                        <RateReviewSharpIcon onClick={handleOpenReviewModal} />
                      </IconButton>
                    )}           
                  </>
                )}
              </div>
            </Tooltip>
          </Typography>
        );
      },
    },
    [mapRequestList, isAdmin],
  );

  const handleRegisterAgain = () => {
    navigate(`/MC/complete-registration`);
  };

  useEffect(() => {
    saveFiltersToLocalStore("mapCenterMapRequestList", filterParams);
  }, [filterParams]);

  return (
    <>
      <Box
        sx={{
          display: { xs: "block", sm: "flex" },
          justifyContent: "space-between",
          alignItems: { sm: "center" },
          height: "60px",
          margin: { xs: "60px 0 120px 0", sm: "0 0 24px 0" },
        }}
      >
        <Box
          sx={{
            width: { xs: "fit-content" },
          }}
        >
          <Typography variant="h2" className="userprofilelistcontent">
            Map Center
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: "16px",
          }}
        >
          <Filter
            companyList={mapRequestList}
            handleFilterSubmit={handleFilterSubmit}
            setRowSelection={setRowSelection}
            filterValues={filterParams}
          />
          {!isAdmin && (
            <Button
              variant="contained"
              className="Download"
              color="primary"
              onClick={handleCreateMapRequestClick}
            >
              <AddCircleOutline />
              &nbsp;Create Request
            </Button>
          )}
        </Box>
      </Box>
      <MaterialReactTable table={table} />
      {isModalOpen ? (
        <ModalPopup
          header="Delete Map Request"
          message1={`Are you sure you want to delete the selected map request${
            selectedMapReqIds.length > 1 ? "s" : ""
          }?
          ${selectedMapReqIds.map((id) => "#" + id)}
          `}
          btnPrimaryText="Confirm"
          btnSecondaryText="Cancel"
          handlePrimaryClick={handleDeleteClick}
          handleSecondaryClick={handleCloseDeleteModal}
        />
      ) : (
        ""
      )}
      {!isAdmin && isMCUserExpired && (
        <ModalPopup
          header="Profile Expired"
          message1="Your profile has expired. Please register again."
          btnPrimaryText="Register Again"
          btnSecondaryText="Cancel"
          handlePrimaryClick={handleRegisterAgain}
          handleSecondaryClick={logout}
          hideBackdrop={false}
        />
      )}
      {!isAdmin && isMCUserPartiallyApproved && (
        <ModalPopup
          header="Profile Partially Approved"
          message1="Your account is partially approved. Click confirm to review the reason and update the registration details."
          btnPrimaryText="Confirm"
          btnSecondaryText="Cancel"
          handlePrimaryClick={handleRegisterAgain}
          handleSecondaryClick={logout}
          hideBackdrop={false}
        />
      )}
      {!isAdmin && isMCUserPartiallyVerified && (
        <ModalPopup
          header="Profile Partially Verified"
          message1="Your account is partially verified. Click confirm to review the reason and update the registration details."
          btnPrimaryText="Confirm"
          btnSecondaryText="Cancel"
          handlePrimaryClick={handleRegisterAgain}
          handleSecondaryClick={logout}
          hideBackdrop={false}
        />
      )}
      <ViewHistory
        open={openHistory}
        setOpen={setOpenHistory}
        mapReqId={historyId}
      />
      <ReviewModal
        mapReqId={historyId}
        open={openReviewModal}
        setOpen={setOpenReviewModal}
        mapReqDetails={mapRequestByIdData}
      />
    </>
  );
};

export default MapView;
